import { create } from "zustand";
import {
  getPlatformRedDot,
  getMerchantRedDot,
  dismissMerchantRedDot,
} from "@/services/platformRedDot";
import { useUserStore } from "@/stores/useUserStore";
import { eventsBus, Events } from "@/utils/eventsBus";

const PLATFORM_LEVEL = 1;
/** 轮询间隔 30 秒 */
const POLLING_INTERVAL = 30000;

interface PlatformRedDotState {
  /** 待审商户数量（平台） */
  operatorApplyPendingCount: number;
  /** 审核中心待审数量（平台） */
  reviewPendingCount: number;
  /** 商户申请未读数量（商户） */
  operatorApplyCount: number;

  loading: boolean;
  pollingTimer: ReturnType<typeof setInterval> | null;

  fetchRedDotData: () => Promise<void>;
  dismissMerchantRedDot: () => Promise<void>;
  startPolling: () => void;
  stopPolling: () => void;
}

export const usePlatformRedDotStore = create<PlatformRedDotState>(
  (set, get) => ({
    operatorApplyPendingCount: 0,
    reviewPendingCount: 0,
    operatorApplyCount: 0,
    loading: false,
    pollingTimer: null,

    fetchRedDotData: async () => {
      const { loading } = get();
      if (loading) return;

      const userInfo = useUserStore.getState().userInfo;
      if (!userInfo) return;

      set({ loading: true });
      try {
        const isPlatform = userInfo.agentLevel === PLATFORM_LEVEL;

        if (isPlatform) {
          const response = await getPlatformRedDot();
          const operatorApplyPendingCount =
            typeof response?.operatorApplyPendingCount === "number"
              ? response.operatorApplyPendingCount
              : Number(response?.operatorApplyPendingCount ?? 0);
          const reviewPendingCount =
            typeof response?.reviewPendingCount === "number"
              ? response.reviewPendingCount
              : Number(response?.reviewPendingCount ?? 0);
          set({
            operatorApplyPendingCount: Number.isFinite(
              operatorApplyPendingCount,
            )
              ? operatorApplyPendingCount
              : 0,
            reviewPendingCount: Number.isFinite(reviewPendingCount)
              ? reviewPendingCount
              : 0,
            loading: false,
          });
        } else {
          const response = await getMerchantRedDot();
          const count =
            typeof response?.operatorApplyCount === "number"
              ? response.operatorApplyCount
              : Number(response?.operatorApplyCount ?? 0);
          set({
            operatorApplyCount: Number.isFinite(count) ? count : 0,
            loading: false,
          });
        }
      } catch (_error) {
        set({ loading: false });
      }
    },

    dismissMerchantRedDot: async () => {
      // 只有代理或商户用户才能调用
      const userInfo = useUserStore.getState().userInfo;
      if (!userInfo || userInfo.agentLevel === PLATFORM_LEVEL) return;

      try {
        await dismissMerchantRedDot();
        set({ operatorApplyCount: 0 });
      } catch (_error) {
        // 静默处理
      }
    },

    startPolling: () => {
      const { pollingTimer } = get();
      if (pollingTimer) return;

      // 立即请求一次
      get().fetchRedDotData();

      const timer = setInterval(() => {
        get().fetchRedDotData();
      }, POLLING_INTERVAL);

      set({ pollingTimer: timer });
    },

    stopPolling: () => {
      const { pollingTimer } = get();
      if (pollingTimer) {
        clearInterval(pollingTimer);
        set({ pollingTimer: null });
      }
    },
  }),
);

// 监听全局审批事件，触发红点数据刷新（节流：3 秒内最多触发一次）
let lastRefreshTime = 0;
const REFRESH_THROTTLE = 3000;

eventsBus.on(Events.REDDOT_REFRESH, () => {
  const now = Date.now();
  if (now - lastRefreshTime < REFRESH_THROTTLE) return;
  lastRefreshTime = now;
  usePlatformRedDotStore.getState().fetchRedDotData();
});
